import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { CoraTicketForm } from "@/components/help/CoraTicketForm";
import { CORA_UI } from "@/constants/cora";
import type { CoraHistoryLine, CoraSupportHours, CoraTurnResponse } from "@/interfaces/cora";

export interface CoraTurnMessageProps {
  turn: CoraTurnResponse;
  onOpenArticle: (slug: string) => void;
  conversationId: string;
  /** History up to and including the user message that led to this turn. */
  history: CoraHistoryLine[];
  screenKey: string | null;
  /** True once this turn's details form has been sent. */
  ticketSent?: boolean;
  onTicketSent?: () => void;
}

const HOURS_FOOTER =
  "m-0 flex items-start gap-2 border-rodeo-dust border-t pt-3 text-[13px] text-graphite";

/** The "team is offline" footer under a Cora line, shown outside support hours. */
export function CoraOutOfHoursNote({ supportHours }: { supportHours: CoraSupportHours }) {
  return (
    <p className={HOURS_FOOTER}>
      <ScheduleOutlinedIcon
        aria-hidden="true"
        sx={{ fontSize: 17 }}
        className="mt-px shrink-0 text-good-ochre"
      />
      {supportHours.outOfHoursMessage}
    </p>
  );
}

/** An article passage (`answer_article`) or Cora's written answer (`answer_generated`), shown
 * with the same citation; a passage is always verbatim and never reworded. */
function CoraArticleAnswer({
  text,
  slug,
  title,
  onOpenArticle,
}: {
  text: string;
  slug: string;
  title: string;
  onOpenArticle: (slug: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 whitespace-pre-line">{text}</p>
      <button
        type="button"
        onClick={() => onOpenArticle(slug)}
        className="flex w-full cursor-pointer flex-col items-start gap-1.5 rounded-[10px] border border-smoke bg-white px-[15px] py-[13px] text-left font-lato transition-colors hover:border-dark-dust focus-visible:outline focus-visible:outline-2 focus-visible:outline-dust-ink focus-visible:outline-offset-2"
      >
        {title && <span className="font-bold text-[#333] text-[13px]">{title}</span>}
        <span className="font-bold text-[11px] text-dust-ink uppercase tracking-[2px]">
          {CORA_UI.OPEN_ARTICLE}
        </span>
      </button>
    </div>
  );
}

/**
 * One Cora turn inside its chat bubble, rendered by its action. Everything is plain text, and an
 * article passage is shown verbatim and never reworded; an answer_generated turn is Cora's
 * written answer from the cited article, shown with the same citation.
 */
export function CoraTurnMessage({
  turn,
  onOpenArticle,
  conversationId,
  history,
  screenKey,
  ticketSent,
  onTicketSent,
}: CoraTurnMessageProps) {
  switch (turn.action) {
    case "answer_article": {
      const { slug, title, passage } = turn.article;
      return (
        <CoraArticleAnswer text={passage} slug={slug} title={title} onOpenArticle={onOpenArticle} />
      );
    }
    case "answer_generated": {
      const { slug, title, answer } = turn.article;
      return (
        <CoraArticleAnswer text={answer} slug={slug} title={title} onOpenArticle={onOpenArticle} />
      );
    }
    case "pass_to_team": {
      const { weekdayOpen, weekdayClose, timezoneLabel } = turn.supportHours;
      return (
        <div className="flex flex-col gap-3">
          {turn.ticketFiled ? (
            <p className="m-0 flex items-start gap-2 text-[13px] text-graphite">
              <CheckCircleOutlineIcon
                aria-hidden="true"
                sx={{ fontSize: 18 }}
                className="mt-px shrink-0 text-good-sage"
              />
              {CORA_UI.HANDOFF_SENT}
            </p>
          ) : (
            <p className="m-0">
              {CORA_UI.HANDOFF_NOT_FILED}{" "}
              <a href={`mailto:${CORA_UI.SUPPORT_EMAIL}`} className="underline">
                {CORA_UI.SUPPORT_EMAIL}
              </a>
              .
            </p>
          )}
          {turn.teamOnline ? (
            <p className={HOURS_FOOTER}>
              <ScheduleOutlinedIcon
                aria-hidden="true"
                sx={{ fontSize: 17 }}
                className="mt-px shrink-0 text-good-ochre"
              />
              {`Our team replies ${weekdayOpen}–${weekdayClose} ${timezoneLabel}, Monday to Friday.`}
            </p>
          ) : (
            <CoraOutOfHoursNote supportHours={turn.supportHours} />
          )}
        </div>
      );
    }
    case "canned":
      return <p className="m-0 whitespace-pre-line">{turn.text}</p>;
    case "ask_details":
      if (ticketSent) {
        return (
          <p role="status" className="m-0 text-[13px] text-graphite">
            {CORA_UI.TICKET_SENT}
          </p>
        );
      }
      return (
        <CoraTicketForm
          conversationId={conversationId}
          ticketType={turn.ticketType}
          questions={turn.questions}
          prefilled={turn.prefilled}
          history={history}
          screenKey={screenKey}
          onSubmitted={onTicketSent}
        />
      );
  }
}

export default CoraTurnMessage;
